/* global React, useState, Icon, Button, Input, Switch, toast, fmt, window */
// ============================================================
// PROTOKOLLFANER — styremøte og generalforsamling
// ============================================================
// Fanene samler moetedataene protokollene trenger. Tallene kommer fra
// aarsregnskapet, deltakerne fra Enhetsregisteret via `companyRoles`, og
// aksjonaerene fra aksjeeierboka. Selve protokollteksten bygges paa backend.

function ProtocolField({ label, children, hint }) {
  return <label className="cl-pf">
    <span className="cl-pf-label">{label}</span>
    {children}
    {hint ? <span className="cl-pf-hint">{hint}</span> : null}
  </label>;
}

// Aksjeeierboka. Aksjonaerregisteret har ingen oppslagstjeneste per selskap, saa
// dette foeres for haand og gjenbrukes senere til Aksjonaerregisteroppgaven.
function ShareRegister({ shareholders, onSave, disabled }) {
  const t = (key, fallback, vars) => (window.PlummeI18n?.t ? window.PlummeI18n.t(key, fallback, vars) : fallback);
  const meetings = window.PlummeAnnualMeetings;
  const [drafts, setDrafts] = useState({});
  const valueFor = (entry, field) => {
    const draft = drafts[`${entry.id}:${field}`];
    return draft === undefined ? entry[field] : draft;
  };
  const commit = (entry, field, raw) => {
    const value = field === 'shares' ? Math.max(0, Math.round(Number(raw) || 0)) : String(raw || '').trim();
    setDrafts((current) => {
      const next = { ...current };
      delete next[`${entry.id}:${field}`];
      return next;
    });
    if (String(entry[field] ?? '') === String(value)) return;
    void onSave(shareholders.map((row) => row.id === entry.id ? { ...row, [field]: value } : row));
  };
  const addShareholder = () => {
    void onSave([...shareholders, { id: `shareholder-new-${Date.now()}`, name: '', shares: 0, present: true }]);
  };

  const total = meetings ? meetings.totalShares(shareholders) : 0;
  const present = meetings ? meetings.presentShares(shareholders) : 0;

  return <div className="cl-box cl-shares">
    <h4 data-help={t('helpTopics.meeting.body')} data-help-title={t('helpTopics.meeting.title')}>{t('clo.shareRegister', 'Aksjonærer')}</h4>
    <p className="t-sm muted">{t('clo.shareRegisterHelp', 'Aksjonærene finnes ikke i noe åpent register, så de føres her. Listen brukes i generalforsamlingsprotokollen.')}</p>
    {shareholders.map((entry) => <div key={entry.id} className="cl-share-row">
      <Input
        placeholder={t('clo.shareholderName', 'Navn')}
        value={valueFor(entry, 'name')}
        disabled={disabled}
        onChange={(event) => setDrafts((c) => ({ ...c, [`${entry.id}:name`]: event.target.value }))}
        onBlur={(event) => commit(entry, 'name', event.target.value)}
      />
      <Input
        type="number"
        min="0"
        className="cl-share-count"
        placeholder={t('clo.shares', 'Aksjer')}
        value={valueFor(entry, 'shares')}
        disabled={disabled}
        onChange={(event) => setDrafts((c) => ({ ...c, [`${entry.id}:shares`]: event.target.value }))}
        onBlur={(event) => commit(entry, 'shares', event.target.value)}
      />
      <Input
        placeholder={t('clo.proxy', 'Møtt av fullmektig')}
        value={valueFor(entry, 'proxy')}
        disabled={disabled}
        onChange={(event) => setDrafts((c) => ({ ...c, [`${entry.id}:proxy`]: event.target.value }))}
        onBlur={(event) => commit(entry, 'proxy', event.target.value)}
      />
      <div className="cl-share-present">
        <Switch
          checked={entry.present}
          disabled={disabled}
          onChange={() => onSave(shareholders.map((row) => row.id === entry.id ? { ...row, present: !row.present } : row))}
        />
        <span className="t-sm muted">{t('clo.present', 'Til stede')}</span>
      </div>
      <Button size="sm" variant="ghost" icon="trash" disabled={disabled}
        onClick={() => onSave(shareholders.filter((row) => row.id !== entry.id))}>
        {t('clo.remove', 'Fjern')}
      </Button>
    </div>)}
    <div className="cl-share-foot">
      <Button size="sm" variant="secondary" icon="plus" disabled={disabled} onClick={addShareholder} data-help={t('helpTopics.meeting.body')} data-help-title={t('helpTopics.meeting.title')}>
        {t('clo.addShareholder', 'Legg til aksjonær')}
      </Button>
      <span className="t-sm">
        <strong>{t('clo.sharesPresent', 'Aksjer til stede')}: {present}</strong>
        {total !== present ? <span className="muted"> / {total}</span> : null}
      </span>
    </div>
  </div>;
}

function AnnualProtocolTab({
  documentKey,
  year,
  meeting,
  people,
  shareholders,
  annualResult,
  disabled,
  onMeetingChange,
  onShareholdersChange,
  onGenerate,
}) {
  const t = (key, fallback, vars) => (window.PlummeI18n?.t ? window.PlummeI18n.t(key, fallback, vars) : fallback);
  const meetings = window.PlummeAnnualMeetings;
  const roles = window.PlummeCompanyRoles;
  const [busy, setBusy] = useState(false);
  if (!meetings || !roles) return null;

  const isBoard = documentKey === 'board';
  const attendees = meetings.meetingAttendees(people, meeting);
  const attendeeIds = attendees.map((person) => person.id);
  const everyone = roles.normalizePeople(people);
  const missing = meetings.missingForProtocol({ documentKey, meetings: { [documentKey]: meeting }, people, shareholders });

  const dividend = Number(meeting.dividend) || 0;
  const retained = Number(annualResult || 0) - dividend;

  const toggleAttendee = (person) => {
    const next = attendeeIds.includes(person.id)
      ? attendeeIds.filter((id) => id !== person.id)
      : [...attendeeIds, person.id];
    onMeetingChange({ attendeeIds: next });
  };

  const generate = async () => {
    setBusy(true);
    try {
      await onGenerate();
      toast(t('clo.protocolGenerated', 'Protokollen er laget'), 'success');
    } catch (error) {
      toast(error?.message || t('clo.protocolFailed', 'Kunne ikke lage protokollen.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const missingLabels = {
    date: t('clo.missingDate', 'dato'),
    location: t('clo.missingLocation', 'sted'),
    attendees: t('clo.missingAttendees', 'deltakere'),
    shareholders: t('clo.missingShareholders', 'aksjonærer'),
    shareholdersPresent: t('clo.missingShareholdersPresent', 'minst én aksjonær til stede'),
  };

  return <div className="cl-protocol">
    <p>{isBoard
      ? t('clo.boardTabHelp', 'Her lager du protokollen fra styremøtet som holdes for å behandle årsregnskapet.')
      : t('clo.generalTabHelp', 'Her lager du protokollen fra generalforsamlingen som godkjenner årsregnskapet.')}</p>

    <div className="cl-box">
      <h4>{t('clo.meetingDetails', 'Møtedetaljer')}</h4>
      <div className="cl-pf-grid">
        <ProtocolField label={t('clo.meetingDate', 'Dato')}>
          <Input type="date" value={meeting.date} disabled={disabled}
            onChange={(event) => onMeetingChange({ date: event.target.value })} />
        </ProtocolField>
        <ProtocolField label={t('clo.meetingTime', 'Tidspunkt')}>
          <Input type="time" value={meeting.time} disabled={disabled}
            onChange={(event) => onMeetingChange({ time: event.target.value })} />
        </ProtocolField>
        <ProtocolField label={t('clo.meetingLocation', 'Sted')}>
          <Input value={meeting.location} disabled={disabled}
            placeholder={t('clo.meetingLocationPh', 'For eksempel Oslo')}
            onChange={(event) => onMeetingChange({ location: event.target.value })} />
        </ProtocolField>
        {!isBoard ? <ProtocolField
          label={t('clo.meetingChair', 'Møteleder')}
          hint={t('clo.meetingChairHint', 'Møteleder og én til signerer protokollen, jf. aksjeloven § 5-16.')}
        >
          <select className="input" value={meeting.chairId || ''} disabled={disabled}
            onChange={(event) => onMeetingChange({ chairId: event.target.value })}>
            <option value="">{t('clo.meetingChairNone', 'Ikke valgt — styret signerer')}</option>
            {everyone.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select>
        </ProtocolField> : null}
      </div>
    </div>

    {isBoard ? <div className="cl-box">
      <h4 data-help={t('helpTopics.meeting.body')} data-help-title={t('helpTopics.meeting.title')}>{t('clo.attendees', 'Deltakere')}</h4>
      <p className="t-sm muted">{t('clo.attendeesHelp', 'Styret er foreslått. Fjern den som ikke møtte — protokollen sier bare at møtet var beslutningsdyktig når flertallet faktisk var til stede.')}</p>
      {everyone.length
        ? everyone.map((person) => <div key={person.id} className="cl-attendee">
          <Switch checked={attendeeIds.includes(person.id)} disabled={disabled} onChange={() => toggleAttendee(person)} />
          <div>
            <strong>{person.name}</strong>
            <span className="t-sm muted"> {roles.roleLabel(roles.primaryRole(person))}</span>
          </div>
        </div>)
        : <p className="t-sm muted">{t('clo.attendeesEmpty', 'Hent rollene fra Enhetsregisteret i oversikten først.')}</p>}
    </div> : <ShareRegister shareholders={shareholders} onSave={onShareholdersChange} disabled={disabled} />}

    <div className="cl-box">
      <h4 data-help={t('helpTopics.meeting.body')} data-help-title={t('helpTopics.meeting.title')}>{t('clo.allocationTitle', 'Disponering av årets resultat for {year}', { year })}</h4>
      <div className="cl-alloc">
        <div className="cl-alloc-row">
          <span>{t('clo.allocResult', 'Årets resultat etter skatt')}</span><strong>{fmt(annualResult)}</strong>
        </div>
        <div className="cl-alloc-row">
          <span><i>-</i>{t('clo.allocDividend', 'Utbetaling av utbytte til eiere')}</span>
          <Input type="number" min="0" className="cl-alloc-input" value={dividend} disabled={disabled}
            onChange={(event) => onMeetingChange({ dividend: Math.max(0, Number(event.target.value) || 0) })} />
        </div>
        <div className="cl-alloc-row strong">
          <span><i>=</i>{retained < 0
            ? t('clo.allocCovered', 'Restbeløp som dekkes av egenkapital')
            : t('clo.allocRetained', 'Restbeløp som overføres til egenkapital')}</span>
          <strong>{fmt(retained)}</strong>
        </div>
      </div>
    </div>

    {missing.length ? <div className="cl-signers-warn">
      <Icon name="info" size={15} />
      <span>{t('clo.protocolMissing', 'Mangler før protokollen kan lages: {fields}', {
        fields: missing.map((key) => missingLabels[key] || key).join(', '),
      })}</span>
    </div> : null}

    <div className="row gap-10">
      <Button icon="download" disabled={disabled || busy || missing.length} onClick={generate}>
        {busy ? t('clo.protocolGenerating', 'Lager protokoll…') : t('clo.generateProtocol', 'Lag protokoll')}
      </Button>
    </div>
  </div>;
}

Object.assign(window, { AnnualProtocolTab, AnnualShareRegister: ShareRegister });
